
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Eye, Star, BookOpen, MessageCircle, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WebNovel, Chapter } from "./types";
import { Card } from "@/components/ui/card";
import { formatDate } from "@/components/webnovel/sidebar/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";

interface WebNovelSidebarProps {
  novel: WebNovel;
  chapter: Chapter;
  user: any;
  navigate: (to: string) => void;
}

export function WebNovelSidebar({ 
  novel, 
  chapter, 
  user, 
  navigate 
}: WebNovelSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");

  const commentsQuery = useQuery({
    queryKey: ["webnovel_comments", novel.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webnovel_comments")
        .select(`
          *,
          profiles:user_id (username, avatar_url)
        `)
        .eq("webnovel_id", novel.id)
        .is("parent_id", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const formatViewCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const navigateToChapter = (chapterNum: number) => {
    if (novel && novel.id) {
      navigate(`/webnovel/${novel.id}/chapter/${chapterNum}`);
    }
  };

  const handleRateWebnovel = async () => {
    if (!user || !rating) return;
    
    try {
      const { error } = await supabase
        .from("webnovel_ratings")
        .upsert({
          user_id: user.id,
          webnovel_id: novel.id,
          rating
        });

      if (error) throw error;
      toast.success("Avaliação salva com sucesso!");
    } catch (error) {
      console.error("Error rating webnovel:", error);
      toast.error("Erro ao salvar avaliação");
    }
  };

  const handleAddComment = async () => {
    if (!user || !comment.trim()) return;
    
    try {
      const { error } = await supabase
        .from("webnovel_comments")
        .insert({
          user_id: user.id,
          webnovel_id: novel.id,
          content: comment.trim()
        });

      if (error) throw error;
      
      setComment("");
      commentsQuery.refetch();
      toast.success("Comentário adicionado com sucesso!");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Erro ao adicionar comentário");
    }
  };

  const handleReply = async (parentId: string) => {
    if (!user || !replyContent.trim()) return;
    
    try {
      const { error } = await supabase
        .from("webnovel_comments")
        .insert({
          user_id: user.id,
          webnovel_id: novel.id,
          content: replyContent.trim(),
          parent_id: parentId
        });

      if (error) throw error;
      
      setReplyContent("");
      setReplyingTo(null);
      commentsQuery.refetch();
      toast.success("Resposta adicionada com sucesso!");
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error("Erro ao adicionar resposta");
    }
  };

  return (
    <div className="lg:col-span-1 space-y-4">
      <Card className="p-4 bg-card rounded-lg border">
        <h3 className="text-lg font-medium mb-3 text-center">Navegação</h3>
        
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const prevChapter = chapter.chapter_number - 1;
              if (prevChapter >= 1) {
                navigateToChapter(prevChapter);
              }
            }}
            disabled={chapter.chapter_number <= 1}
          >
            Anterior
          </Button>
          
          <span className="text-sm">
            Capítulo {chapter.chapter_number} de {novel.chapters?.length || 1}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const nextChapter = chapter.chapter_number + 1;
              navigateToChapter(nextChapter);
            }}
          >
            Próximo
          </Button>
        </div>
        
        <div className="text-center">
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => setSidebarOpen(true)}
            className="w-full"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Todos os Capítulos
          </Button>
        </div>
      </Card>
      
      <Card className="p-4 bg-card rounded-lg border">
        <h3 className="text-lg font-medium mb-4 text-center">Sobre a WebNovel</h3>
        
        <div className="flex gap-3 mb-4">
          <div className="flex-shrink-0">
            <img
              src={novel.cover_url}
              alt={novel.title}
              className="w-20 h-28 object-cover rounded-md"
            />
          </div>
          
          <div className="flex-1">
            <h4 className="text-base font-semibold">{novel.title}</h4>
            <div className="flex items-center text-sm text-muted-foreground mt-1">
              <span className="mr-1">Aka</span>
              <span className="font-medium">{novel.author?.username || "Autor Desconhecido"}</span>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="px-2 py-0.5 bg-muted/60 rounded-md text-xs">
                {novel.category || "Ficção"}
              </span>
              <span className="px-2 py-0.5 bg-muted/60 rounded-md text-xs">
                {novel.age_rating || "Livre"}
              </span>
            </div>
            
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
              {novel.description || "Nenhuma descrição disponível."}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-1 mt-2">
          <div className="flex flex-col items-center p-2 rounded-md bg-muted/50">
            <Star className="h-4 w-4 text-yellow-500 mb-1" />
            <span className="text-sm font-medium">{novel.average_rating?.toFixed(1) || "N/A"}</span>
            <span className="text-xs text-muted-foreground">{novel.ratings_count || 0} avaliações</span>
          </div>
          
          <div className="flex flex-col items-center p-2 rounded-md bg-muted/50">
            <Eye className="h-4 w-4 mb-1" />
            <span className="text-sm font-medium">{novel.views || 0}</span>
            <span className="text-xs text-muted-foreground">visualizações</span>
          </div>
          
          <div className="flex flex-col items-center p-2 rounded-md bg-muted/50">
            <CalendarDays className="h-4 w-4 mb-1" />
            <span className="text-sm font-medium">{formatDate(novel.created_at).split(" ")[0]}</span>
            <span className="text-xs text-muted-foreground">publicação</span>
          </div>
        </div>
      </Card>
      
      <Card className="p-4 bg-card rounded-lg border">
        <h3 className="text-lg font-medium mb-4 text-center">Avaliação</h3>
        
        <div className="flex justify-center mb-3">
          {[1, 2, 3, 4, 5].map((star) => (
            <Button 
              key={star} 
              variant="ghost" 
              size="sm" 
              onClick={() => setRating(star)}
              className="p-0 h-auto"
            >
              <Star 
                className="w-6 h-6 text-yellow-500" 
                fill={rating >= star ? "currentColor" : "transparent"}
              />
            </Button>
          ))}
        </div>
        
        <Button 
          className="w-full" 
          onClick={handleRateWebnovel}
          disabled={!rating || !user}
        >
          Avaliar
        </Button>
      </Card>
      
      <Card className="p-4 bg-card rounded-lg border">
        <h3 className="text-lg font-medium mb-3 text-center">Comentários</h3>
        
        <div className="mb-3">
          <textarea 
            className="w-full border border-border bg-background rounded-md p-3 text-sm"
            placeholder="Escreva seu comentário..."
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        
        <Button 
          variant="default" 
          className="w-full mb-4"
          onClick={handleAddComment}
          disabled={!comment.trim() || !user}
        >
          Enviar Comentário
        </Button>
        
        <ScrollArea className="h-[400px]">
          {commentsQuery.data?.map((comment: any) => (
            <div key={comment.id} className="mb-4 p-3 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={comment.profiles?.avatar_url} />
                  <AvatarFallback>
                    {comment.profiles?.username?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{comment.profiles?.username}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(comment.created_at)}
                  </p>
                </div>
              </div>
              
              <p className="text-sm mb-2">{comment.content}</p>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                >
                  Responder
                </Button>
              </div>
              
              {replyingTo === comment.id && (
                <div className="mt-2">
                  <textarea 
                    className="w-full border border-border bg-background rounded-md p-2 text-sm"
                    placeholder="Escreva sua resposta..."
                    rows={2}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent("");
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleReply(comment.id)}
                      disabled={!replyContent.trim()}
                    >
                      Enviar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </ScrollArea>
      </Card>
      
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="flex flex-row justify-between items-center">
            <SheetTitle>Capítulos</SheetTitle>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div 
              className="p-3 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => {
                navigateToChapter(chapter.chapter_number);
                setSidebarOpen(false);
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Capítulo {chapter.chapter_number}</p>
                  <p className="text-sm text-muted-foreground">{chapter.title}</p>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(chapter.created_at)}
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
